/**
 * PublicMenuContent — main visible content for PublicMenuPage.
 * Covers: sticky header, status/banners, category pills, search, product catalog, cart sidebar, mobile cart bar.
 * Sub-components: MenuHeader (header bar), MenuCatalog (catalog + sidebar).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingBag, MapPin, Clock, Gift, Share2, Menu, AlertTriangle, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { BannerCarousel } from "@/components/public/MenuComponents";
import { ReviewsSection } from "@/components/public/ReviewsSection";
import { TableSessionBanner } from "@/components/public/TableSessionBanner";
import { resolveMedia } from "@/utils/mediaUtils";
import { MenuHeader } from "@/components/public/MenuHeader";
import { MenuCatalog } from "@/components/public/MenuCatalog";
import { bcTrack } from "@/utils/behavioralCommerce";

const DEFAULT_WHATSAPP = "34673036835";

function hexToRgb(hex) {
  const h = (hex || "#ffffff").replace("#", "");
  return [parseInt(h.substring(0, 2), 16), parseInt(h.substring(2, 4), 16), parseInt(h.substring(4, 6), 16)];
}

export function PublicMenuContent({
  restaurant, restaurantId, isTableOrder, restaurantStatus, smartPricing, schedulingMode, deepLinkSlug, suppressAutoOpen,
  menuSearchBanners = [],
  tc, isDark, cardBg, cardBorder, catColor, priceColor, logoUrl, logoClass, bannerVideoUrl,
  tableName, tableNumber,
  customerSession, showReorderBanner, setShowReorderBanner, setShowAuthModal, handleReorder, customerPhone,
  tablePayAtEnd, tableSession, billRequested, showCheckout, orderConfirmed, requestBill,
  refreshTableSession, setShowTableOrdersModal, requestingBill,
  categories, products, combos,
  cart, setCart, cartTotal, cartCount, addToCart, addToCartSilent, updateQuantity,
  suggestions, trackUpsell,
  openCheckout, setShowReservation, setReservationConfirmed, setShowAllergenInfo,
  fmt, API,
}) {
  const { t } = useTranslation();
  const [pr, pg, pb] = hexToRgb(tc.primary);
  const allAllergens = [...new Set(products.flatMap(p => p.allergens || []))];

  const [highlights, setHighlights] = useState(null);
  useEffect(() => {
    if (!restaurantId) return;
    fetch(`${API}/public/menu-highlights?restaurant_id=${restaurantId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setHighlights(data); })
      .catch(() => { /* silent — badges are non-critical */ });
  }, [restaurantId, API]);

  // Push subscription: ask once after 8 seconds, silently
  useEffect(() => {
    if (!restaurantId || !("serviceWorker" in navigator) || !("PushManager" in window)) return;
    if (localStorage.getItem(`push_asked_${restaurantId}`)) return;
    const timer = setTimeout(async () => {
      try {
        localStorage.setItem(`push_asked_${restaurantId}`, "1");
        const vapidRes = await fetch(`${API}/push/vapid-key`);
        const { public_key } = await vapidRes.json();
        if (!public_key) return;
        const reg = await navigator.serviceWorker.register("/sw-push.js");
        await navigator.serviceWorker.ready;
        const existing = await reg.pushManager.getSubscription();
        if (existing) return;
        const perm = await Notification.requestPermission();
        if (perm !== "granted") return;
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: (() => {
            const pad = "=".repeat((4 - (public_key.length % 4)) % 4);
            const b64 = (public_key + pad).replace(/-/g, "+").replace(/_/g, "/");
            const raw = window.atob(b64);
            return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
          })(),
        });
        const phone = customerPhone || "";
        await fetch(`${API}/push/subscribe-customer-general`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ phone: phone || `anon_${restaurantId}_${Date.now()}`, restaurant_id: restaurantId, subscription: sub.toJSON() }),
        });
      } catch { /* silent — never break the menu */ }
    }, 8000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  return (
    <>
      <MenuHeader
        restaurant={restaurant} tc={tc} pr={pr} pg={pg} pb={pb}
        tableName={tableName} tableNumber={tableNumber}
        cart={cart} cartTotal={cartTotal} cartCount={cartCount} openCheckout={openCheckout} fmt={fmt}
        customerSession={customerSession} setShowAuthModal={setShowAuthModal}
        setShowReservation={setShowReservation} setReservationConfirmed={setReservationConfirmed}
        logoUrl={logoUrl} logoClass={logoClass}
      />

      {/* WhatsApp status is internal — never expose technical errors to public customers */}

      <div className="max-w-5xl mx-auto px-3 sm:px-4 pt-3">
        <BannerCarousel banners={(restaurant?.theme?.banners || []).map(resolveMedia)} restaurantName={restaurant?.name} restaurantDescription={restaurant?.description || ""} logoUrl={logoUrl} videoUrl={bannerVideoUrl || ""} logoFormat={restaurant?.theme?.logo_format || "round"} />
      </div>

      {/* Status Badge + Action Buttons */}
      <div className="max-w-5xl mx-auto px-3 sm:px-4 pt-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {restaurantStatus === 'open' && <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-green-100 text-green-700 border border-green-200" data-testid="status-open"><span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>{t("restaurant.open")}</span>}
            {restaurantStatus === 'pickup_only' && <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700 border border-amber-200" data-testid="status-pickup-only"><span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>{t("publicMenu.openForPickup")}</span>}
            {restaurantStatus === 'closed' && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200" data-testid="status-closed"><span className="w-2 h-2 rounded-full bg-red-500"></span>{t("restaurant.closed")}</span>
            )}
            {restaurantStatus === 'closed' && schedulingMode !== 'disabled' && (
              <span className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200" data-testid="can-schedule-badge">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                {t("publicMenu.canSchedule")}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <a href="#menu-section" className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition shadow-sm border" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'white', borderColor: tc.primary, color: tc.primary }} data-testid="view-menu-btn"><Menu className="w-4 h-4" />{t("publicMenu.viewMenu")}</a>
            {(!isTableOrder && restaurant?.delivery_enabled === false && restaurant?.pickup_enabled === false) ? (
              <span
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 border border-gray-200"
                data-testid="no-online-orders-badge"
              >
                <ShoppingBag className="w-4 h-4" />
                {t("publicMenu.noOnlineOrders", "Pedidos online indisponíveis")}
              </span>
            ) : (
              <button
                onClick={() => cart.length > 0 ? openCheckout() : document.getElementById('menu-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white transition shadow-sm hover:opacity-90 active:opacity-80"
                style={{ backgroundColor: tc.primary }}
                data-testid="order-now-btn"
              >
                <ShoppingBag className="w-4 h-4" />
                {restaurantStatus === 'closed' && schedulingMode !== 'disabled'
                  ? (cart.length > 0 ? t("publicMenu.scheduleWithCount", { count: cart.length }) : t("publicMenu.scheduleOrder"))
                  : (cart.length > 0 ? t("publicMenu.orderNowWithCount", { count: cart.length }) : t("publicMenu.orderNow"))
                }
              </button>
            )}
          </div>
        </div>
      </div>

      {smartPricing?.active && (
        <div className="max-w-5xl mx-auto px-3 sm:px-4 pt-3" data-testid="smart-pricing-banner">
          <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl p-3 text-white flex items-center justify-between shadow-lg">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-black">-{smartPricing.discount}%</span>
              <div><p className="font-bold text-sm">{smartPricing.message || t("publicMenu.smartPricingDefault", { discount: smartPricing.discount })}</p><p className="text-xs text-red-100">{t("publicMenu.smartPricingAutoApplied")}</p></div>
            </div>
            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">{smartPricing.day_name}</span>
          </div>
        </div>
      )}

      {/* Info bar */}
      <div className="max-w-5xl mx-auto px-3 sm:px-4 py-2">
        <div data-touch-pan-x="" className="flex items-center gap-3 sm:gap-6 text-[11px] sm:text-xs overflow-x-auto scrollbar-hide whitespace-nowrap" style={{ color: isDark ? 'rgba(255,255,255,0.6)' : '#6b7280' }}>
          {restaurant?.delivery_enabled !== false && (
            <span className="flex items-center gap-1" data-testid="delivery-info-badge">
              <MapPin className="w-3 h-3 flex-shrink-0" style={{ color: tc.primary }} />
              <span className="font-medium" style={{ color: tc.primary }}>{restaurant?.delivery_zones?.some(z => z.active !== false && z.free_above != null) ? "Entrega según distancia y valor del pedido" : (restaurant?.delivery_free_km > 0 ? t("delivery.freeDelivery", { km: restaurant.delivery_free_km }) : t("delivery.freeDeliveryAlways"))}</span>
              {restaurant?.default_shipping_fee > 0 && <span style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#9ca3af' }} className="ml-0.5">| +{fmt(restaurant?.default_shipping_fee)} apos</span>}
            </span>
          )}
          {restaurant?.min_order_amount > 0 && (
            <span className="flex items-center gap-1" style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#6b7280' }} data-testid="min-order-badge">
              <span className="font-medium">{t("publicMenu.minOrderLabel")}: {fmt(restaurant?.min_order_amount)}</span>
            </span>
          )}
          <a href={`https://wa.me/${(restaurant?.phone || DEFAULT_WHATSAPP).replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" onClick={() => bcTrack("whatsapp_click")} className="flex items-center gap-1 hover:opacity-80 transition" data-testid="header-whatsapp-link">
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-green-500" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            <span className="font-semibold text-green-600">{(restaurant?.phone || DEFAULT_WHATSAPP).replace(/^\+?34/, "").replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3")}</span>
          </a>
          <span className="flex items-center gap-1"><Clock className="w-3 h-3 flex-shrink-0" style={{ color: tc.primary }} /><span className="font-medium" style={{ color: tc.primary }}>{(() => { const mn = restaurant?.wait_time_min || 30; const mx = restaurant?.wait_time_max || 50; return mn === mx ? `~${mn} min` : `~${mn}-${mx} min`; })()}</span></span>
          {allAllergens.length > 0 && (
            <button onClick={() => setShowAllergenInfo(true)} className="flex items-center gap-1 text-amber-600 hover:text-amber-700 font-medium flex-shrink-0" data-testid="allergen-info-btn"><AlertTriangle className="w-3 h-3" />{t("publicMenu.allergenInfo")}</button>
          )}
        </div>
      </div>

      {/* Reviews */}
      <div className="max-w-5xl mx-auto px-3 sm:px-4 py-3">
        <ReviewsSection restaurantId={restaurantId} themeColors={tc} />
      </div>

      {/* Table Session Banner */}
      {tablePayAtEnd && tableSession && !showCheckout && !orderConfirmed && !billRequested && (
        <TableSessionBanner session={tableSession} onRequestBill={requestBill} onViewOrders={() => { refreshTableSession(); setShowTableOrdersModal(true); }} isRequesting={requestingBill} themeColor={tc.primary} />
      )}

      {/* Reorder Banner */}
      {customerSession?.found && customerSession.last_orders?.length > 0 && showReorderBanner && !showCheckout && !orderConfirmed && (
        <div className="border-b" style={{ backgroundColor: `${tc.primary}08`, borderColor: `${tc.primary}20` }}>
          <div className="max-w-5xl mx-auto px-3 sm:px-4 py-2.5">
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold truncate" style={{ color: tc.text }}>{t("publicMenu.helloCustomer", { name: customerSession.name || "" })}</p>
                <p className="text-[11px] truncate" style={{ color: `${tc.text}80` }}>{customerSession.last_orders[0].items?.map(i => i.product_name || i.name).join(", ")}</p>
              </div>
              {customerSession.loyalty_enabled && customerSession.points > 0 && (
                <div className="text-center flex-shrink-0 px-2">
                  <p className="text-sm font-black" style={{ color: tc.primary }}>{customerSession.points}</p>
                  <p className="text-[9px]" style={{ color: `${tc.text}60` }}>{t("publicMenu.points")}</p>
                </div>
              )}
              <button onClick={() => handleReorder(customerSession.last_orders[0])} className="flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold text-white" style={{ backgroundColor: tc.primary }} data-testid="reorder-btn">{t("orders.reorder")}</button>
              <button onClick={() => setShowReorderBanner(false)} className="flex-shrink-0 opacity-40 hover:opacity-70" style={{ color: tc.text }}><X className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        </div>
      )}

      {/* Referral sharing */}
      {customerSession && customerPhone && (
        <div className="mx-3 mt-2" data-testid="referral-share-section">
          <button
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/referral/customer-code/${restaurantId}/${encodeURIComponent(customerPhone)}`);
                const code = res.data.code;
                const shareText = t("publicMenu.referralShareText", { code, discount: res.data.discount_percent });
                if (navigator.share) { await navigator.share({ title: restaurant?.name || "Kaiso", text: shareText }); }
                else { await navigator.clipboard.writeText(code); toast.success(t("publicMenu.copiedCode", { code })); }
              } catch { toast.error(t("publicMenu.errors.getCode")); }
            }}
            className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border text-left"
            style={{ borderColor: `${tc.primary}30`, backgroundColor: `${tc.primary}08` }}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Gift className="w-4 h-4 flex-shrink-0" style={{ color: tc.primary }} />
              <span className="text-xs font-bold truncate" style={{ color: tc.primary }}>{t("publicMenu.referralCta")}</span>
            </div>
            <Share2 className="w-4 h-4 flex-shrink-0" style={{ color: tc.primary }} />
          </button>
        </div>
      )}

      <MenuCatalog
        tc={tc} isDark={isDark} cardBg={cardBg} cardBorder={cardBorder} catColor={catColor} priceColor={priceColor}
        categories={categories} products={products} combos={combos}
        cart={cart} setCart={setCart} cartTotal={cartTotal} cartCount={cartCount}
        addToCart={addToCart} addToCartSilent={addToCartSilent} updateQuantity={updateQuantity}
        suggestions={suggestions} trackUpsell={trackUpsell}
        openCheckout={openCheckout}
        smartPricing={smartPricing} restaurant={restaurant} fmt={fmt}
        highlights={highlights} deepLinkSlug={deepLinkSlug} suppressAutoOpen={suppressAutoOpen}
        searchBanners={menuSearchBanners.map((url) => resolveMedia(url))}
      />

      {/* Mobile cart bar — hidden while checkout dialog is open to avoid z-index overlap with DialogFooter */}
      {cart.length > 0 && !showCheckout && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 p-2.5 sm:p-3 backdrop-blur-sm border-t shadow-xl z-[1001]" style={{ backgroundColor: isDark ? 'rgba(5,5,5,0.95)' : 'rgba(255,255,255,0.95)', borderColor: cardBorder }}>
          <Button onClick={openCheckout} className="w-full h-12 text-white font-bold text-sm rounded-xl shadow-lg" style={{ backgroundColor: tc.primary }} data-testid="mobile-checkout-btn">
            <ShoppingBag className="w-4 h-4 mr-2" />{t('cart.checkout')} - {fmt(cartTotal)}<Badge className="ml-2 bg-white/20 text-white">{cartCount}</Badge>
          </Button>
        </div>
      )}

      {/* Customer Portal Footer */}
      <div className="max-w-5xl mx-auto px-3 sm:px-4 py-6 mt-4 border-t" style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#f0f0f0' }}>
        <a href={`/portal/${restaurantId}`} className="flex items-center justify-center gap-2 text-sm font-semibold px-4 py-3 rounded-xl border transition hover:opacity-80" style={{ color: tc.primary, borderColor: `${tc.primary}40`, backgroundColor: `${tc.primary}08` }} data-testid="customer-portal-link">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
          {t("publicMenu.customerPortalCta")}
        </a>
      </div>
    </>
  );
}
